import { api } from '@/lib/api';
import {
  type ComposerAttachment,
  attachmentId,
  attachmentToChip,
  uploadedFiles,
} from '@/lib/attachment';
import { cn } from '@/lib/cn';
import { useServerSpeechRecognition } from '@/lib/useServerSpeechRecognition';
import { useSpeechRecognition } from '@/lib/useSpeechRecognition';
import type { ChatModel, FileUpload } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowUp, Loader2, Mic, MicOff, Plus, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AttachFilesDialog } from './AttachFilesDialog';
import { AttachmentChips } from './AttachmentChips';
import { ModelPicker } from './ModelPicker';

/** 메시지 1개당 동시 첨부 한도. 서버 zod (`CreateChatMessageRequestSchema.fileIds.max(10)`) 와 일치. */
export const COMPOSER_MAX_ATTACHMENTS = 10;

interface Props {
  onSend: (content: string) => void;
  pending: boolean;
  /** 시작 시 자동 포커스 여부. 기본 true. */
  autoFocus?: boolean;
  attachments: ComposerAttachment[];
  onAttachmentsChange: (next: ComposerAttachment[]) => void;
  /** 현재 세션 id. 첨부 다이얼로그를 이 세션의 파일만 보이도록 스코프. 인덱스 화면은 undefined. */
  sessionId?: string;
  /**
   * `uploaded` 칩의 최신 파일 룩업. 부모가 폴링하는 파일 목록에서 파생. 넘기면 다이얼로그
   * 선택 시점의 동결 스냅샷 대신 실시간 status/queueAhead 로 칩 phase·대기순번·경과가 갱신된다.
   */
  liveById?: Map<string, FileUpload>;
  /** 모델 선택기 — 제공 시 composer 우하에 표시. */
  modelPicker?: {
    models: ChatModel[];
    selected: ChatModel | null;
    extendedThinking: boolean;
    onSelect: (m: ChatModel) => void;
    onToggleThinking: (v: boolean) => void;
  };
  /** 진행 중 응답 중단. 제공 + pending 일 때 보내기 버튼이 정지 버튼으로 바뀐다. */
  onStop?: () => void;
}

export function ChatComposer({
  onSend,
  pending,
  autoFocus = true,
  attachments,
  onAttachmentsChange,
  sessionId,
  liveById,
  modelPicker,
  onStop,
}: Props) {
  const [value, setValue] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  // === 3-tier 음성 입력 ===
  // 1) browser-native (Web Speech API) — Chrome / Edge / Safari iOS
  const browserSpeech = useSpeechRecognition({ lang: 'ko-KR' });
  // 2) capability probe — browser 가 미지원일 때만 서버에 물어봄
  const capQuery = useQuery({
    queryKey: ['stt', 'capabilities'],
    queryFn: api.sttCapabilities,
    enabled: !browserSpeech.supported,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const serverSttAvailable = capQuery.data?.serverStt === true;
  // 3) server fallback (MediaRecorder + POST /api/stt/transcribe)
  const serverSpeech = useServerSpeechRecognition({
    lang: 'ko-KR',
    enabled: !browserSpeech.supported && serverSttAvailable,
  });

  // 우선순위: browser → server → null. null 이면 마이크 버튼 자체 안 보임.
  const speech = browserSpeech.supported
    ? browserSpeech
    : serverSpeech.supported
      ? serverSpeech
      : null;

  // 말하기 시작할 때의 textarea 값 — final transcript 가 누적될 때 baseline 으로 사용.
  const speechBaseRef = useRef<string>('');

  // final 이 갱신될 때 baseline + final 로 textarea 동기화 (interim 은 별도 placeholder 로 표시).
  useEffect(() => {
    if (!speech) return;
    if (!speech.listening && !speech.finalTranscript) return;
    const base = speechBaseRef.current;
    const merged = speech.finalTranscript
      ? base
        ? `${base} ${speech.finalTranscript}`
        : speech.finalTranscript
      : base;
    setValue(merged);
  }, [speech?.finalTranscript, speech?.listening, speech]);

  // 녹음 종료 시 reset 해서 다음 녹음에 영향 없게.
  useEffect(() => {
    if (!speech) return;
    if (!speech.listening && speech.finalTranscript) {
      // finalTranscript 가 이미 value 에 반영됐으니 hook 내부 상태만 비움.
      speech.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speech?.listening]);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  // textarea 자동 높이 조절 — value 가 바뀔 때마다 scrollHeight 재계산
  // biome-ignore lint/correctness/useExhaustiveDependencies: value 는 height 재계산 트리거 용도
  useEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 240)}px`;
  }, [value]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || pending) return;
    onSend(trimmed);
    setValue('');
    ref.current?.focus();
  };

  const toggleMic = () => {
    if (!speech) return;
    if (speech.listening) {
      speech.stop();
      return;
    }
    speech.reset();
    // 녹음 시작 시점의 textarea 값을 baseline 으로 저장 — 그 뒤 들리는 말은 그 뒤에 붙는다.
    speechBaseRef.current = value;
    speech.start();
  };

  const removeAttachment = (id: string) => {
    onAttachmentsChange(attachments.filter((a) => attachmentId(a) !== id));
  };

  const chips = attachments.map((a) => attachmentToChip(a, liveById));

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="w-full max-w-3xl mx-auto"
      >
        <div className="rounded-2xl border border-border bg-surface shadow-[var(--shadow)] p-3">
          {chips.length > 0 ? <AttachmentChips files={chips} onRemove={removeAttachment} /> : null}
          <textarea
            ref={ref}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
            rows={1}
            placeholder={speech?.listening ? '듣는 중… 말씀하세요' : '메시지를 입력하세요…'}
            className="w-full resize-none bg-transparent px-2 py-1.5 text-base outline-none placeholder:text-foreground-muted"
          />
          {speech?.listening && speech.interimTranscript ? (
            <div className="px-2 pb-1 text-sm text-foreground-muted italic">
              {speech.interimTranscript}
            </div>
          ) : null}
          {speech?.error ? (
            <div className="px-2 pb-1 text-xs text-destructive">{speech.error}</div>
          ) : null}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={pending}
              aria-label="파일 첨부"
              className="size-8 grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="size-4" />
            </button>
            <div className="flex items-center gap-2">
              {pending ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
                  <Loader2 className="size-3 animate-spin" aria-hidden />
                  응답 생성 중…
                </span>
              ) : null}
              {modelPicker ? (
                <ModelPicker
                  models={modelPicker.models}
                  selected={modelPicker.selected}
                  extendedThinking={modelPicker.extendedThinking}
                  onSelect={modelPicker.onSelect}
                  onToggleThinking={modelPicker.onToggleThinking}
                  disabled={pending}
                />
              ) : null}
              {speech ? (
                <button
                  type="button"
                  onClick={toggleMic}
                  disabled={pending}
                  aria-label={speech.listening ? '녹음 중지' : '음성 입력'}
                  title={
                    speech.listening
                      ? '녹음 중지'
                      : browserSpeech.supported
                        ? '음성으로 입력 (브라우저 내장)'
                        : '음성으로 입력 (서버 처리)'
                  }
                  className={cn(
                    'size-8 grid place-items-center rounded-md transition-colors',
                    'disabled:opacity-40 disabled:cursor-not-allowed',
                    speech.listening
                      ? 'bg-destructive/15 text-destructive animate-pulse'
                      : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
                  )}
                >
                  {speech.listening ? <MicOff className="size-4" /> : <Mic className="size-4" />}
                </button>
              ) : null}
              {pending && onStop ? (
                <button
                  type="button"
                  onClick={onStop}
                  aria-label="응답 중단"
                  title="응답 중단"
                  className={cn(
                    'size-8 grid place-items-center rounded-full bg-foreground text-background transition-opacity',
                    'hover:opacity-90',
                  )}
                >
                  <Square className="size-3" fill="currentColor" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={pending || value.trim().length === 0}
                  aria-label="보내기"
                  className={cn(
                    'size-8 grid place-items-center rounded-full bg-accent text-accent-foreground transition-opacity',
                    'disabled:opacity-40 disabled:cursor-not-allowed',
                    !pending && value.trim().length > 0 && 'hover:opacity-90',
                  )}
                >
                  <ArrowUp className="size-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </form>
      <AttachFilesDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedIds={uploadedFiles(attachments).map((f) => f.id)}
        maxAttachments={COMPOSER_MAX_ATTACHMENTS}
        sessionId={sessionId}
        pendingFiles={uploadedFiles(attachments)}
        onUploaded={(file) => {
          if (attachments.some((a) => a.kind === 'uploaded' && a.file.id === file.id)) {
            return;
          }
          if (attachments.length >= COMPOSER_MAX_ATTACHMENTS) return;
          onAttachmentsChange([...attachments, { kind: 'uploaded', file }]);
        }}
        onConfirm={(files) => {
          // 라이브러리 모드 confirm 은 선택된 업로드 파일 목록을 통째로 교체. pending 항목은 유지.
          const pendings = attachments.filter((a) => a.kind === 'pending');
          const next: ComposerAttachment[] = [
            ...pendings,
            ...files.map((f) => ({ kind: 'uploaded' as const, file: f })),
          ];
          onAttachmentsChange(next);
        }}
      />
    </>
  );
}
